// © 2026 AttainIP Engineering
import { orgChoiceLabel, orgChoiceSubLabel } from '@/lib/orgChoiceLabel';
import { orgSurfaceQualifier } from '@/lib/orgSurfaceQualifier';
import { rowOrgChoice, type UserDirectoryRow } from '@/lib/userDirectoryRow';

/**
 * The "Organisation" cell of the admin Users list (RFC-0510 W41).
 *
 * A user row exists per (person, organisation) — RFC-0510 A7 — so this is what
 * makes one row a different thing from the next. It reuses the
 * Select-Organisation labelling wholesale, so a firm reads the same here as it
 * does in the picker, including the two cases a naive `org.name` would get
 * wrong: a personal organisation (no name, no readable ext_id) and one the API
 * could not resolve at all, which degrades to an obviously-technical
 * `Organisation <first 8>` rather than a blank cell.
 *
 * ⚠️ ONE THING IT DOES NOT REUSE: the SURFACE (RFC-0510 F37). The picker's
 * sub-label strips an ext_id's prefix, so `enterprise-domain:rothl.com` and
 * `email-domain:rothl.com` — two organisations that cannot see each other's
 * data — both printed as "Rothl / rothl.com". Right in the picker, where a
 * human is choosing between firms and the prefix is noise; wrong here, where
 * it is the only thing separating one row from the next. The qualifier is
 * appended HERE rather than inside the shared helper, so a firm still reads
 * identically in the directory and in the picker.
 */
export function UserRowOrganisation({ row }: { row: UserDirectoryRow }) {
  const choice = rowOrgChoice(row);
  const sub = orgChoiceSubLabel(choice);
  const surface = orgSurfaceQualifier(choice.org_type);
  // Only ever a suffix on a line that already exists: with no readable ext_id
  // there is no second line to qualify, and inventing one to carry the
  // qualifier alone would put a bare word under a name with nothing to attach
  // it to.
  const subLine = sub && surface ? `${sub} · ${surface}` : sub;

  return (
    <>
      <div>{orgChoiceLabel(choice)}</div>
      {subLine && <div className="text-xs text-gray-400 font-mono">{subLine}</div>}
    </>
  );
}
